/**
 * useDiscardRfpDraft
 *
 * A reusable mutation hook for discarding RFP drafts with optimistic updates.
 * Supports two query invalidation strategies:
 * 1. RFP list queries (["rfps", "drafts"]) - for main RFP pages
 * 2. Client queries (["client", clientId]) - for client-specific RFP pages
 *
 * @param options - Configuration for query key handling and error management
 * @returns Mutation object and discard handler
 */
import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@platform/api";

import { extractErrorMessage } from "../utils";
import { submitEvent } from "../utils/tracking";

import useToastNotificationService from "./useToastNotificationService";
import { getClientQueryKey } from "./useClient";

import type { RfpsListResponse, RfpListItem } from "./useRfpsList";
import type { Client } from "../types/clientTab";

interface DraftsQueryCacheEntry {
  key: readonly unknown[];
  data: RfpsListResponse | undefined;
}

interface ClientMutationContext {
  previousClient?: Client;
}

interface RfpListMutationContext {
  previous: DraftsQueryCacheEntry[];
}

type MutationContext = ClientMutationContext | RfpListMutationContext;

interface UseDiscardRfpDraftOptions {
  /**
   * Query invalidation strategy
   * - "rfp-list": Invalidates all ["rfps", "drafts"] queries (for main RFP pages)
   * - "client": Invalidates specific client query (for client-specific pages)
   */
  queryStrategy: "rfp-list" | "client";

  /**
   * Client ID - required when queryStrategy is "client"
   */
  clientId?: string;

  /**
   * Error setter callback
   */
  setError: (error: string | null) => void;
}

interface UseDiscardRfpDraftReturn {
  /**
   * The mutation object from React Query
   */
  mutation: ReturnType<
    typeof useMutation<string, unknown, string, MutationContext>
  >;

  /**
   * Handler function to discard a draft by ID
   */
  handleDiscardRfpDraft: (rfpId: string) => void;

  /**
   * Whether the mutation is currently pending
   */
  isPending: boolean;
}

export function useDiscardRfpDraft(
  options: UseDiscardRfpDraftOptions,
): UseDiscardRfpDraftReturn {
  const { queryStrategy, clientId, setError } = options;
  const axiosInstance = useApi();
  const queryClient = useQueryClient();
  const { createToastNotification } = useToastNotificationService();

  // Compute client query key once to avoid repeated function calls
  const clientQueryKey = clientId ? getClientQueryKey(clientId) : undefined;

  const mutation = useMutation<string, unknown, string, MutationContext>({
    mutationFn: async (rfpId: string): Promise<string> => {
      submitEvent("RFP Draft Deleted", { rfp_id: rfpId });
      await axiosInstance.post(`brokerportal/v1/deleteRfpDraft`, { rfpId });
      return rfpId;
    },

    onMutate: async (rfpId: string) => {
      setError?.(null);

      if (queryStrategy === "client" && clientQueryKey) {
        // Client-specific optimistic update
        await queryClient.cancelQueries({ queryKey: clientQueryKey });

        const previousClient = queryClient.getQueryData<Client>(clientQueryKey);

        if (previousClient) {
          const rfpIdNum = parseInt(rfpId, 10);
          queryClient.setQueryData<Client>(clientQueryKey, {
            ...previousClient,
            draftRfpList: previousClient.draftRfpList.filter(
              (draft) => draft.id !== rfpIdNum,
            ),
          });
        }

        return { previousClient } as ClientMutationContext;
      }
      // RFP list optimistic update
      const draftsKey = ["rfps", "drafts"] as const;
      await queryClient.cancelQueries({ queryKey: draftsKey, exact: false });

      const keys = queryClient
        .getQueryCache()
        .getAll()
        .filter((q) => {
          const k = q.queryKey as unknown[];
          return Array.isArray(k) && k[0] === "rfps" && k[1] === "drafts";
        });

      const previous: DraftsQueryCacheEntry[] = keys.map((entry) => ({
        key: entry.queryKey as readonly unknown[],
        data: entry.state.data as RfpsListResponse | undefined,
      }));

      keys.forEach((entry) => {
        const data = entry.state.data as RfpsListResponse | undefined;
        if (data?.results) {
          queryClient.setQueryData(entry.queryKey, {
            ...data,
            results: data.results.filter((r: RfpListItem) => r.id !== rfpId),
          });
        }
      });

      return { previous } as RfpListMutationContext;
    },

    onError: (err: unknown, rfpId: string, context) => {
      // Rollback optimistic update
      if (queryStrategy === "client" && clientQueryKey && context) {
        const clientContext = context as ClientMutationContext;
        if (clientContext.previousClient) {
          queryClient.setQueryData(
            clientQueryKey,
            clientContext.previousClient,
          );
        }
      } else if (context) {
        const rfpListContext = context as RfpListMutationContext;
        rfpListContext.previous?.forEach((p) =>
          queryClient.setQueryData(p.key, p.data),
        );
      }

      setError?.(
        extractErrorMessage(
          err,
          `There was an error discarding RFP ${rfpId} Draft.`,
        ),
      );
    },

    onSuccess: () => {
      createToastNotification({
        severity: "success",
        message: "RFP Draft discarded",
      });
    },

    onSettled: () => {
      // Invalidate appropriate queries
      if (queryStrategy === "client" && clientQueryKey) {
        queryClient.invalidateQueries({
          queryKey: clientQueryKey,
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["rfps", "drafts"],
          exact: false,
        });
      }
    },
  });

  const handleDiscardRfpDraft = useCallback(
    (rfpId: string): void => mutation.mutate(rfpId),
    [mutation],
  );

  return {
    mutation,
    handleDiscardRfpDraft,
    isPending: mutation.isPending,
  };
}
